import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../api/client';
import endpoints from '../../api/endpoints';
import LoadingSpinner from '../LoadingSpinner';
import { CheckCircle, XCircle, Eye, Trash2, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';

const FeedbackTab = () => {
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterIssueType, setFilterIssueType] = useState('');

  const { data: feedbackData, isLoading, refetch } = useQuery(
    ['admin-feedback', filterStatus, filterIssueType],
    () => {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterIssueType) params.append('issueType', filterIssueType);
      return api.get(`${endpoints.feedback.list}?${params}`).then(res => res.data);
    }
  );

  const handleUpdateStatus = async (feedbackId, newStatus) => {
    try {
      await api.patch(endpoints.feedback.updateStatus(feedbackId), { status: newStatus });
      toast.success(`Feedback ${newStatus}`);
      refetch();
      if (selectedFeedback?._id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
      toast.error('Failed to update feedback status');
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    try {
      await api.delete(endpoints.feedback.delete(feedbackId));
      toast.success('Feedback deleted');
      refetch();
      if (selectedFeedback?._id === feedbackId) {
        setSelectedFeedback(null);
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast.error('Failed to delete feedback');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'warning', label: 'Pending' },
      reviewed: { class: 'info', label: 'Reviewed' },
      approved: { class: 'success', label: 'Approved' },
      rejected: { class: 'danger', label: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  const getIssueTypeLabel = (type) => {
    const labels = {
      factual_error: 'Factual Error',
      incomplete: 'Incomplete',
      tone: 'Tone/Bias',
      hallucination: 'Hallucination',
      formatting: 'Formatting',
      other: 'Other'
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading feedback..." />;
  }

  const filteredFeedback = feedbackData?.feedback || [];

  return (
    <div className="feedback-tab">
      <div className="tab-section-header">
        <h2 className="tab-section-title">Response Feedback</h2>
        <div className="tab-actions">
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            {filteredFeedback.filter(f => f.status === 'pending').length} pending review
          </span>
        </div>
      </div>

      <div className="filters-bar">
        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <select
          className="filter-select"
          value={filterIssueType}
          onChange={(e) => setFilterIssueType(e.target.value)}
        >
          <option value="">All Issue Types</option>
          <option value="factual_error">Factual Error</option>
          <option value="incomplete">Incomplete</option>
          <option value="tone">Tone/Bias</option>
          <option value="hallucination">Hallucination</option>
          <option value="formatting">Formatting</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedFeedback ? '1fr 1fr' : '1fr', gap: 'var(--spacing-lg)' }}>
        <div>
          {filteredFeedback.length === 0 ? (
            <div className="empty-state">
              <AlertCircle size={48} className="empty-state-icon" />
              <h3 className="empty-state-title">No Feedback</h3>
              <p className="empty-state-text">No feedback reports found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Issue Type</th>
                  <th>Query</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeedback.map(feedback => (
                  <tr key={feedback._id} style={{ background: selectedFeedback?._id === feedback._id ? 'var(--color-background)' : 'transparent' }}>
                    <td>{getIssueTypeLabel(feedback.issueType)}</td>
                    <td>
                      <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {feedback.query}
                      </div>
                      <small style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>
                        {new Date(feedback.createdAt).toLocaleDateString()}
                      </small>
                    </td>
                    <td>{getStatusBadge(feedback.status)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="action-button"
                          onClick={() => setSelectedFeedback(feedback)}
                          title="View details"
                        >
                          <Eye size={14} />
                        </button>
                        {feedback.status !== 'approved' && (
                          <button
                            className="action-button primary"
                            onClick={() => handleUpdateStatus(feedback._id, 'approved')}
                            title="Approve"
                          >
                            <CheckCircle size={14} />
                          </button>
                        )}
                        {feedback.status !== 'rejected' && (
                          <button
                            className="action-button danger"
                            onClick={() => handleUpdateStatus(feedback._id, 'rejected')}
                            title="Reject"
                          >
                            <XCircle size={14} />
                          </button>
                        )}
                        <button
                          className="action-button danger"
                          onClick={() => handleDeleteFeedback(feedback._id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedFeedback && (
          <div className="feedback-viewer">
            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>{getIssueTypeLabel(selectedFeedback.issueType)}</h3>
                <button
                  className="action-button"
                  onClick={() => setSelectedFeedback(null)}
                >
                  Close
                </button>
              </div>
              <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                Reported: {new Date(selectedFeedback.createdAt).toLocaleString()}
              </p>
              <div style={{ marginTop: '8px' }}>
                {getStatusBadge(selectedFeedback.status)}
              </div>
            </div>

            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <strong style={{ display: 'block', marginBottom: '8px' }}>User Query:</strong>
              <div style={{ background: 'var(--color-background)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                {selectedFeedback.query}
              </div>
            </div>

            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <strong style={{ display: 'block', marginBottom: '8px' }}>Original Response:</strong>
              <div style={{ background: '#fee', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', border: '1px solid #fcc', maxHeight: '200px', overflowY: 'auto' }}>
                <ReactMarkdown>{selectedFeedback.response}</ReactMarkdown>
              </div>
            </div>

            {selectedFeedback.adminCorrection && (
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <strong style={{ display: 'block', marginBottom: '8px' }}>Corrected Response:</strong>
                <div style={{ background: '#efe', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', border: '1px solid #cfc', maxHeight: '200px', overflowY: 'auto' }}>
                  <ReactMarkdown>{selectedFeedback.adminCorrection}</ReactMarkdown>
                </div>
              </div>
            )}

            {selectedFeedback.adminNotes && (
              <div>
                <strong style={{ display: 'block', marginBottom: '8px' }}>Admin Notes:</strong>
                <div style={{ background: 'var(--color-background)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontStyle: 'italic' }}>
                  {selectedFeedback.adminNotes}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackTab;
